import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardBody, CardTitle, Col, Row } from 'reactstrap'
import ReactECharts from 'echarts-for-react'
import Request from '../../../helpers/request'
import * as url from '../../../helpers/url_helper'
import { fwDisplay } from '../../../helpers/fw'

// Fleet status rows (Ram, 2026-08-04, single-page rework):
//   Row A: Modules-by-SKU vertical card · fleet firmware donut · per-SKU
//          version minis · [Auto-OTA + Automation stacked]
//   Row B: Appliances (control donut + class one-liner) · Last heard ·
//          [Knob modes + Assembled stacked]
// Production charts moved to the /production-inventory tab (Ram: "production
// can be done later; more records than shown here") — the Assembled card
// links there. Dropped as redundant: the SKU spread donut (identical data to
// the vertical card) and the Appliances-by-class band (folded into the
// Appliances card as one line). Small cards are STACKED into shared columns
// so the available space is fully used. Every card hides when its data
// source is absent — never a broken chart.

const STATUS_COLORS: { [k: string]: string } = {
    current: '#2ecc71', behind: '#f5b041', unknown: '#e74c3c', unclassified: '#95a5a6',
}
const FRESHNESS_LABELS: { [k: string]: string } = {
    day: 'Today', week: '7 d', month: '30 d', older: 'Older', never: 'Never',
}
const TITLE_STYLE = { color: '#5D6679', fontWeight: 500, fontSize: 15 }
const DENSE_BODY = { padding: 14 }
const CARD_STYLE = { borderRadius: 10 }

type Props = {
    bySku: { [sku: string]: number } | null
    moduleMeta: { field: number; total: number; placeholders: number; retired: number } | null
    byClass: { [k: string]: number } | null
}

const FleetCharts = ({ bySku, moduleMeta, byClass }: Props) => {
    const navigate = useNavigate()
    const [fwSplit, setFwSplit] = useState<any[] | null>(null)
    const [freshness, setFreshness] = useState<any>(null)
    const [otaJobs, setOtaJobs] = useState<any>(null)
    const [applianceSplit, setApplianceSplit] = useState<any>(null)
    const [extras, setExtras] = useState<any>(null)
    const [production, setProduction] = useState<any>(null)
    const [otaStatus, setOtaStatus] = useState<any>(null)
    const [otaFailed, setOtaFailed] = useState<any>(null)

    useEffect(() => {
        Request.get(url.GET_DASHBOARD_CHARTS).then((r: any) => {
            setFwSplit(r?.data?.fwSplit || null)
            setFreshness(r?.data?.freshness || null)
            setOtaJobs(r?.data?.otaJobs || null)
            setApplianceSplit(r?.data?.applianceSplit || null)
            setExtras(r?.data?.extras || null)
            setProduction(r?.data?.production || null)
        }).catch(() => { /* old backend — hide the charts */ })
        Request.post(url.OTA_STATUS, {}).then((r: any) => setOtaStatus(r?.data?.data || null))
            .catch(() => {})
        Request.post(url.OTA_FAILED, {}).then((r: any) => setOtaFailed(r?.data?.data || null))
            .catch(() => {})
    }, [])

    // Fleet-level Updated/Older/Others donut.
    const statusTotals = (fwSplit && fwSplit.length) ? fwSplit.reduce((acc: any, p: any) => {
        for (const v of p.versions) {
            const k = v.status === 'current' ? 'updated' : (v.status === 'behind' ? 'older' : 'others')
            acc[k] = (acc[k] || 0) + v.count
        }
        return acc
    }, {}) : null
    const FLEET_SLICES: { [k: string]: string } = { updated: '#2ecc71', older: '#f5b041', others: '#e74c3c' }
    const fleetOption = statusTotals ? {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        series: [{
            type: 'pie', radius: ['45%', '72%'],
            label: { formatter: '{b}\n{c}', fontSize: 10 },
            data: Object.keys(FLEET_SLICES)
                .filter((k) => (statusTotals[k] || 0) > 0)
                .map((k) => ({ name: k, value: statusTotals[k], itemStyle: { color: FLEET_SLICES[k] } })),
        }],
    } : null

    // One mini chart per SKU with its OWN scale — a combined axis let
    // DaFlip's count suppress every other SKU. FW displayed as semver.
    const MAX_VERSION_ROWS = 5
    const mkMini = (versions: any[], colorFor: (v: any) => string) => {
        const rows = [...versions].sort((a: any, b: any) => b.count - a.count).slice(0, MAX_VERSION_ROWS).reverse()
        return {
            tooltip: {},
            grid: { left: 44, right: 8, top: 2, bottom: 16 },
            xAxis: { type: 'value', axisLabel: { fontSize: 9 }, minInterval: 1 },
            yAxis: { type: 'category', data: rows.map((v: any) => v.version === 'unset' ? 'unset' : fwDisplay(v.version)), axisLabel: { fontSize: 9 } },
            series: [{
                type: 'bar', barMaxWidth: 10,
                data: rows.map((v: any) => ({ value: v.count, itemStyle: { color: colorFor(v) } })),
            }],
        }
    }
    const perSku = (fwSplit && fwSplit.length) ? fwSplit.map((p: any) => ({
        sku: p.sku,
        total: p.total,
        more: Math.max(0, p.versions.length - MAX_VERSION_ROWS),
        option: mkMini(p.versions, (v) => STATUS_COLORS[v.status] || '#95a5a6'),
    })) : null

    const freshOption = freshness ? {
        tooltip: {},
        grid: { left: 36, right: 10, top: 12, bottom: 20 },
        // interval: 0 — echarts auto-hides alternating labels in narrow cards,
        // which read as "missing names" (Ram, 2026-08-04). Short labels fit.
        xAxis: { type: 'category', data: Object.keys(FRESHNESS_LABELS).map((k) => FRESHNESS_LABELS[k]), axisLabel: { interval: 0, fontSize: 10 } },
        yAxis: { type: 'value', axisLabel: { fontSize: 9 } },
        series: [{
            type: 'bar',
            data: Object.keys(FRESHNESS_LABELS).map((k) => ({
                value: freshness.buckets?.[k] || 0,
                itemStyle: { color: k === 'never' ? '#e74c3c' : (k === 'older' ? '#f5b041' : '#5D6679') },
            })),
        }],
    } : null

    const CONTROL_LABELS: { [k: string]: { name: string; color: string } } = {
        onoff: { name: 'On/Off', color: '#5D6679' },
        level: { name: 'Level', color: '#4AA3DF' },
        curtain: { name: 'Curtain', color: '#7A6FBE' },
        unbound: { name: 'Unbound', color: '#B8BFC9' },
    }
    const controlOption = applianceSplit ? {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        series: [{
            type: 'pie', radius: ['45%', '72%'],
            label: { formatter: '{b}\n{c}', fontSize: 10 },
            data: Object.keys(CONTROL_LABELS)
                .filter((k) => (applianceSplit[k] || 0) > 0)
                .map((k) => ({
                    name: CONTROL_LABELS[k].name,
                    value: applianceSplit[k],
                    itemStyle: { color: CONTROL_LABELS[k].color },
                })),
        }],
    } : null

    const jobs = otaJobs?.byState || null
    const policy = otaStatus?.policy || null
    const failedCount = otaFailed?.count ?? (Array.isArray(otaFailed?.failed) ? otaFailed.failed.length : null)
    const CLASS_LINE = byClass
        ? ['light', 'fan', 'curtain', 'socket', 'other']
            .map((k) => `${k.charAt(0).toUpperCase()}${k.slice(1)} ${byClass[k] || 0}`)
            .join(' · ')
        : null

    if (!bySku && !fleetOption && !freshOption && !policy && failedCount === null) return null

    const smallP = { marginBottom: 4, fontSize: 13, color: '#858D9D' } as const
    const captionP = { fontSize: 11, color: '#B8BFC9', marginBottom: 0 } as const

    const otaCard = (policy || failedCount !== null) ? (
        <Card style={CARD_STYLE}>
            <CardBody style={DENSE_BODY}>
                <CardTitle style={TITLE_STYLE}>Auto-OTA</CardTitle>
                {policy ? (
                    <>
                        <p style={smallP}>
                            mode <b style={{ color: '#5D6679' }}>{policy.mode}</b>
                        </p>
                        <p style={smallP}>
                            opted-in <b style={{ color: '#5D6679' }}>{policy.allowedPlaces?.length ?? '—'}</b>
                            {' · '}exempt <b style={{ color: '#5D6679' }}>{policy.deniedPlaces?.length ?? '—'}</b>
                        </p>
                    </>
                ) : null}
                {jobs ? (
                    <p style={smallP}>
                        jobs: <span style={{ color: '#2ecc71' }}>{jobs.success || 0} ok</span>
                        {' · '}<span style={{ color: '#f5b041' }}>{(jobs.pending || 0) + (jobs.verifying || 0)} open</span>
                    </p>
                ) : null}
                {failedCount !== null ? (
                    <p style={{ marginBottom: 0, fontSize: 13 }}>
                        <a href="/ota" style={{ color: failedCount ? '#e74c3c' : '#858D9D' }}>
                            couldn&apos;t update: {failedCount}
                        </a>
                    </p>
                ) : null}
            </CardBody>
        </Card>
    ) : null

    const automationCard = extras ? (
        <Card style={CARD_STYLE}>
            <CardBody style={DENSE_BODY}>
                <CardTitle style={TITLE_STYLE}>Automation</CardTitle>
                <p style={smallP}>groups <b style={{ color: '#5D6679' }}>{extras.groups}</b></p>
                <p style={smallP}>scenes <b style={{ color: '#5D6679' }}>{extras.scenes}</b></p>
                <p style={{ ...smallP, marginBottom: 0 }}>bells <b style={{ color: '#5D6679' }}>{extras.bells}</b></p>
            </CardBody>
        </Card>
    ) : null

    const knobCard = extras?.knobModes ? (
        <Card style={CARD_STYLE}>
            <CardBody style={DENSE_BODY}>
                <CardTitle style={TITLE_STYLE}>Knob modes</CardTitle>
                <p style={smallP}>5-Step <b style={{ color: '#5D6679' }}>{extras.knobModes.fiveStep}</b></p>
                <p style={smallP}>Detent <b style={{ color: '#5D6679' }}>{extras.knobModes.detent}</b></p>
                <p style={smallP}>Smooth <b style={{ color: '#5D6679' }}>{extras.knobModes.smooth}</b></p>
                <p style={captionP}>unset {extras.knobModes.unset} (run the 5-Step default)</p>
            </CardBody>
        </Card>
    ) : null

    const assembledCard = production?.assembled ? (
        <Card style={CARD_STYLE}>
            <CardBody style={DENSE_BODY}>
                <CardTitle style={TITLE_STYLE}>Assembled</CardTitle>
                <p style={smallP}>total <b style={{ color: '#5D6679' }}>{production.assembled.total}</b></p>
                {production.assembled.pass === 0 && production.assembled.other === production.assembled.total ? (
                    // EMS records pass/fail per-MAC in the testing records, not
                    // on the assembled row — overallResult is blank fleet-wide.
                    <p style={captionP}>result not recorded at assembly</p>
                ) : (
                    <p style={smallP}>
                        pass <b style={{ color: '#2ecc71' }}>{production.assembled.pass}</b>
                        {' · '}other <b style={{ color: '#5D6679' }}>{production.assembled.other}</b>
                    </p>
                )}
                <p style={{ marginBottom: 0, fontSize: 13 }}>
                    <a href="/production-inventory" onClick={(e) => { e.preventDefault(); navigate('/production-inventory') }} style={{ color: '#4AA3DF' }}>
                        full production inventory →
                    </a>
                </p>
            </CardBody>
        </Card>
    ) : null

    return (
        <>
        <Row>
            {bySku ? (
                <Col xl="2" lg="3" md="6" sm="12">
                    <Card style={CARD_STYLE}>
                        <CardBody style={DENSE_BODY}>
                            <CardTitle style={TITLE_STYLE}>Modules by SKU</CardTitle>
                            {Object.entries(bySku).map(([sku, n]) => (
                                <div key={sku} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#858D9D', marginBottom: 3 }}>
                                    <span>{sku}</span>
                                    <b style={{ color: '#5D6679' }}>{n as any}</b>
                                </div>
                            ))}
                            {moduleMeta ? (
                                <p style={{ ...captionP, marginTop: 8 }}>
                                    {moduleMeta.total} rows total — {moduleMeta.field} field, {moduleMeta.placeholders} placeholder sockets, {moduleMeta.retired} retired
                                </p>
                            ) : null}
                        </CardBody>
                    </Card>
                </Col>
            ) : null}
            {fleetOption ? (
                <Col xl="2" lg="3" md="6" sm="12">
                    <Card style={CARD_STYLE}>
                        <CardBody style={DENSE_BODY}>
                            <CardTitle style={TITLE_STYLE}>Firmware status (fleet)</CardTitle>
                            <ReactECharts option={fleetOption} style={{ height: 165 }} />
                            <p style={{ ...captionP, textAlign: 'center' }}>
                                updated = on latest · older = OTA pending · others = unknown FW
                            </p>
                        </CardBody>
                    </Card>
                </Col>
            ) : null}
            {perSku ? (
                <Col xl="5" lg="6" md="12">
                    <Card style={CARD_STYLE}>
                        <CardBody style={DENSE_BODY}>
                            <CardTitle style={TITLE_STYLE}>Firmware versions by SKU</CardTitle>
                            <Row>
                                {perSku.map((m: any) => (
                                    <Col xs="6" key={m.sku}>
                                        <div style={{ fontSize: 11, color: '#858D9D', marginBottom: 1 }}>
                                            {m.sku} · {m.total}{m.more ? ` (+${m.more} more)` : ''}
                                        </div>
                                        <ReactECharts option={m.option} style={{ height: 88 }} />
                                    </Col>
                                ))}
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            ) : null}
            {(otaCard || automationCard) ? (
                <Col xl="3" lg="6" md="6" sm="12">
                    {otaCard}
                    {automationCard}
                </Col>
            ) : null}
        </Row>
        <Row>
            {controlOption ? (
                <Col xl="4" lg="6" md="6" sm="12">
                    <Card style={CARD_STYLE}>
                        <CardBody style={DENSE_BODY}>
                            <CardTitle style={TITLE_STYLE}>Appliances (control types)</CardTitle>
                            <ReactECharts option={controlOption} style={{ height: 160 }} />
                            {CLASS_LINE ? (
                                <p style={{ ...captionP, textAlign: 'center' }}>{CLASS_LINE}</p>
                            ) : null}
                        </CardBody>
                    </Card>
                </Col>
            ) : null}
            {freshOption ? (
                <Col xl="4" lg="6" md="6" sm="12">
                    <Card style={CARD_STYLE}>
                        <CardBody style={DENSE_BODY}>
                            <CardTitle style={TITLE_STYLE}>
                                Last heard ({freshness.total} field modules)
                            </CardTitle>
                            <ReactECharts option={freshOption} style={{ height: 160 }} />
                            <p style={{ ...captionP, textAlign: 'center' }}>
                                grey = seen within 30 d · <span style={{ color: '#f5b041' }}>amber = stale (30 d+)</span> · <span style={{ color: '#e74c3c' }}>red = never heard</span>
                            </p>
                        </CardBody>
                    </Card>
                </Col>
            ) : null}
            {(knobCard || assembledCard) ? (
                <Col xl="4" lg="6" md="6" sm="12">
                    <Row>
                        <Col md="6" sm="6">{knobCard}</Col>
                        <Col md="6" sm="6">{assembledCard}</Col>
                    </Row>
                </Col>
            ) : null}
        </Row>
        </>
    )
}

export default FleetCharts
